import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

interface ApiEndpoint {
  method: "GET" | "POST" | "PUT";
  path: string;
  auth: "operator" | "session" | "public" | "admin";
  blurb: string;
  request?: string;
  response: string;
  errors: Array<[number, string]>;
  curl: string;
}

const PUBLIC_ENDPOINTS: ApiEndpoint[] = [
  {
    method: "POST",
    path: "/public/sandbox/operator",
    auth: "public",
    blurb: "Self-serve sandbox key issuance. Rate-limited to 3 per IP per 24 h. Keys carry the vb_sbx_ prefix and live for 14 days.",
    request: `{
  companyName: string,
  email:       string
}`,
    response: `{
  operatorId: string,
  apiKey:     string,    // vb_sbx_…  (shown ONCE)
  expiresAt:  string     // ISO-8601, ~14d from issuance
}`,
    errors: [
      [400, "validation failure (missing field, malformed email)"],
      [429, "rate limit — 3 sandbox keys per IP per 24 h"],
    ],
    curl: `curl -X POST https://api.vaultbreaker.dev/public/sandbox/operator \\
  -H "content-type: application/json" \\
  -d '{"companyName":"Acme Casino","email":"dev@acme.example"}'`,
  },
  {
    method: "GET",
    path: "/public/sandbox/operator/me",
    auth: "operator",
    blurb: "Read sandbox operator status — id, expiry, remaining TTL. Use to verify a key is still live before launching a session.",
    response: `{
  operatorId: string,
  tier:       "sandbox",
  expiresAt:  string,
  expired:    boolean
}`,
    errors: [
      [401, "missing or invalid X-API-Key"],
      [404, "key not a sandbox key (use /public/operator/me instead)"],
    ],
    curl: `curl https://api.vaultbreaker.dev/public/sandbox/operator/me \\
  -H "X-API-Key: vb_sbx_…"`,
  },
  {
    method: "GET",
    path: "/public/operator/me",
    auth: "operator",
    blurb: "Read production operator status — id, tier, payout-wallet status. Companion to the sandbox endpoint above for live keys.",
    response: `{
  operatorId:    string,
  tier:          "live" | "sandbox",
  payoutWallet:  string | null,
  webhookCount:  number
}`,
    errors: [[401, "missing or invalid X-API-Key"]],
    curl: `curl https://api.vaultbreaker.dev/public/operator/me \\
  -H "X-API-Key: vb_live_…"`,
  },
];

const ENDPOINTS: ApiEndpoint[] = [
  {
    method: "POST",
    path: "/rgs/session/launch",
    auth: "operator",
    blurb: "Mint a session for one player. Server seed is committed here; only the SHA-256 hash leaves.",
    request: `{
  operatorId: string,
  playerId:   string,
  currency:   "BTC" | "ETH" | "SOL" | "USDT" | "USD",
  locale:     string  // default "en"
}`,
    response: `{
  sessionId:      string,
  token:          string,        // short-lived JWT for WebSocket auth
  balance:        number,
  currency:       Currency,
  clientSeed:     string,         // server-generated, 16 hex
  serverSeedHash: string          // 64 hex (SHA-256 commitment)
}`,
    errors: [
      [400, "validation failure (bad currency, missing field)"],
      [401, "missing or invalid X-API-Key"],
      [403, "operator suspended or terminated"],
      [404, "operator id not registered"],
    ],
    curl: `curl -X POST https://vaultbreaker-api.fly.dev/rgs/session/launch \\
  -H "x-api-key: vb_YOUR_KEY" \\
  -H "content-type: application/json" \\
  -d '{"operatorId":"acme","playerId":"p1","currency":"BTC"}'`,
  },
  {
    method: "POST",
    path: "/rgs/round/open",
    auth: "session",
    blurb: "Debit the wallet. Bumps the nonce. Pre-rolls the round deterministically.",
    request: `{
  sessionId:  string,
  betAmount:  number   // > 0, finite, ≤ MAX_BET
}`,
    response: `{
  roundId:        string,
  serverSeedHash: string,
  clientSeed:     string,
  nonce:          number,
  balance:        number    // post-debit
}`,
    errors: [
      [400, "betAmount NaN, ≤ 0, or > platform cap"],
      [402, "insufficient balance"],
      [404, "session not found"],
      [409, "round already active for this session"],
    ],
    curl: `curl -X POST https://vaultbreaker-api.fly.dev/rgs/round/open \\
  -H "content-type: application/json" \\
  -d '{"sessionId":"sess_…","betAmount":0.001}'`,
  },
  {
    method: "POST",
    path: "/rgs/round/resolve",
    auth: "session",
    blurb: "Cash out at the chosen multiplier. Server is the clock authority — if the multiplier exceeds the bust point, outcome is bust.",
    request: `{
  sessionId:         string,
  roundId:           string,
  cashoutMultiplier: number  // > 0
}`,
    response: `{
  roundId:           string,
  outcome:           "win" | "bust",
  cashoutMultiplier: number,
  payout:            number,
  balance:           number
}`,
    errors: [
      [400, "validation"],
      [404, "session or round not found"],
      [409, "round already resolved (idempotent — returns the same outcome)"],
    ],
    curl: `curl -X POST https://vaultbreaker-api.fly.dev/rgs/round/resolve \\
  -H "content-type: application/json" \\
  -d '{"sessionId":"sess_…","roundId":"rnd_…","cashoutMultiplier":1.5}'`,
  },
  {
    method: "POST",
    path: "/rgs/round/rollback",
    auth: "session",
    blurb: "Idempotent reversal for disputes. Returns betAmount to the wallet, clears payout. Safe to retry.",
    request: `{ sessionId, roundId, reason? }`,
    response: `{ roundId, rolledBack: boolean, balance: number }`,
    errors: [
      [400, "validation"],
      [404, "round not found"],
    ],
    curl: `curl -X POST https://vaultbreaker-api.fly.dev/rgs/round/rollback \\
  -H "content-type: application/json" \\
  -d '{"sessionId":"sess_…","roundId":"rnd_…","reason":"network drop"}'`,
  },
  {
    method: "GET",
    path: "/rgs/balance",
    auth: "session",
    blurb: "Read-only wallet check. Equivalent to the value returned by the most recent open / resolve / rollback.",
    response: `{ sessionId, balance, currency }`,
    errors: [[404, "session not found"]],
    curl: `curl https://vaultbreaker-api.fly.dev/rgs/balance?sessionId=sess_…`,
  },
  {
    method: "GET",
    path: "/rgs/round/:id/seeds",
    auth: "public",
    blurb: "Reveal the server seed AFTER the round resolves. Before resolution returns 64 zeros for the seed (the hash is unchanged).",
    response: `{
  roundId, serverSeed, serverSeedHash,
  clientSeed, nonce, bustMultiplier,
  revealed: boolean
}`,
    errors: [[404, "round not found"]],
    curl: `curl https://vaultbreaker-api.fly.dev/rgs/round/rnd_abc/seeds`,
  },
  {
    method: "POST",
    path: "/rgs/verify",
    auth: "public",
    blurb: "Independently re-derive a round outcome from disclosed seeds. Useful for player-side audits.",
    request: `{
  serverSeed:             string,
  clientSeed:             string,
  nonce:                  number,
  claimedBustMultiplier:  number,
  claimedServerSeedHash:  string
}`,
    response: `{
  valid:           boolean,
  hashMatch:       boolean,
  multiplierMatch: boolean,
  recomputed:      number,
  recomputedHash:  string
}`,
    errors: [[400, "validation"]],
    curl: `curl -X POST https://vaultbreaker-api.fly.dev/rgs/verify \\
  -H "content-type: application/json" \\
  -d @seeds.json`,
  },
];

const ADMIN_ENDPOINTS: ApiEndpoint[] = [
  {
    method: "POST",
    path: "/admin/operators/:id/webhooks",
    auth: "admin",
    blurb: "Register or rotate the webhook endpoint for an operator. The shared secret is stored once; rotate by re-POSTing.",
    request: `{
  url:    string,
  events: Array<"round.opened" | "round.resolved" | "session.launched" | "settlement.recorded">,
  secret: string
}`,
    response: `{
  webhookId: string,
  url:       string,
  events:    string[],
  createdAt: string
}`,
    errors: [
      [400, "validation (bad URL, unknown event, empty secret)"],
      [401, "missing or invalid admin token"],
      [404, "operator not found"],
    ],
    curl: `curl -X POST https://vaultbreaker-api.fly.dev/admin/operators/acme/webhooks \\
  -H "authorization: Bearer <admin token>" \\
  -H "content-type: application/json" \\
  -d '{"url":"https://wallet.acme/wh","events":["round.resolved"],"secret":"whsec_…"}'`,
  },
  {
    method: "PUT",
    path: "/admin/operators/:id/payout-wallet",
    auth: "admin",
    blurb: "Set or update the operator settlement wallet address. We send weekly settlement payouts here.",
    request: `{
  address:  string,    // wallet address
  currency: "BTC" | "ETH" | "SOL" | "USDT" | "USDC" | "USD",
  network?: string     // optional, e.g. "ERC20" | "TRC20" | "SOLANA"
}`,
    response: `{
  operatorId: string,
  address:    string,
  currency:   string,
  network:    string | null,
  updatedAt:  string
}`,
    errors: [
      [400, "validation (malformed address for currency, unsupported network)"],
      [401, "missing or invalid admin token"],
      [404, "operator not found"],
    ],
    curl: `curl -X PUT https://vaultbreaker-api.fly.dev/admin/operators/acme/payout-wallet \\
  -H "authorization: Bearer <admin token>" \\
  -H "content-type: application/json" \\
  -d '{"address":"TXYZ…","currency":"USDT","network":"TRC20"}'`,
  },
  {
    method: "GET",
    path: "/admin/webhook-deliveries",
    auth: "admin",
    blurb: "List webhook deliveries — successes, failures, retries. Filter by operator, event type, status, or date range.",
    response: `{
  deliveries: Array<{
    id:           string,    // X-VB-Delivery
    operatorId:   string,
    event:        string,
    url:          string,
    status:       "delivered" | "failed" | "pending",
    attempts:     number,    // 1 or 2
    lastStatus:   number | null,
    lastError:    string | null,
    createdAt:    string,
    deliveredAt:  string | null
  }>,
  cursor: string | null
}`,
    errors: [[401, "missing or invalid admin token"]],
    curl: `curl "https://vaultbreaker-api.fly.dev/admin/webhook-deliveries?status=failed&limit=50" \\
  -H "authorization: Bearer <admin token>"`,
  },
];

const WS_EVENTS: Array<[string, string, string]> = [
  ["round:state", "phase transition", '{ roundId, phase: "betting"|"live"|"win"|"bust", startedAt? }'],
  ["tick", "30 fps multiplier update", "{ roundId, tMs, multiplier }"],
  ["crack", "one of five locks cracks", "{ roundId, index: 0..4, tMs }"],
  ["resolved", "final outcome", "{ roundId, outcome, multiplier, payout }"],
  ["error", "server-side error", "{ message }"],
];

export function ApiReference() {
  return (
    <>
      <Seo
        title="API reference · Vault Breaker Studios"
        description="REST and WebSocket contract for the Vault Breaker RGS — endpoints, request/response shapes, error codes, and curl examples."
        path="/api-reference"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          API REFERENCE
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          API reference
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          Six REST endpoints, one WebSocket stream, one verify route. The full surface fits on
          a page. Every request and response is typed in{" "}
          <code className="mono" style={{ color: "var(--cyan)" }}>@vb/rgs-contract</code> via Zod.
        </p>
      </Section>

      <Section id="auth">
        <SectionHeading eyebrow="Auth" title="Four call modes" />
        <Card style={{ display: "grid", gap: 14 }}>
          <AuthRow
            scope="operator"
            header="X-API-Key: vb_..."
            note="Server-to-server only. Hashed (SHA-256) and looked up against operators.api_key_hash. Sandbox keys use the vb_sbx_ prefix; live keys use vb_live_."
          />
          <AuthRow
            scope="session"
            header="Body must include valid sessionId"
            note="Round-level calls. The sessionId itself is the auth — keep it server-side."
          />
          <AuthRow
            scope="admin"
            header="Authorization: Bearer <admin token>"
            note="Internal admin endpoints — webhook config, payout-wallet updates, delivery audit. Provisioned per-operator during onboarding."
          />
          <AuthRow
            scope="public"
            header="(no auth)"
            note="Sandbox key issuance, verify, and seed-reveal endpoints — exposed so any third party can audit a round or self-serve a sandbox key."
          />
        </Card>
      </Section>

      <Section id="public">
        <SectionHeading
          eyebrow="Public · sandbox + status"
          title="Self-serve onboarding"
          lead="Issue a sandbox key, then poll your operator status. No admin token required."
        />
        <div style={{ display: "grid", gap: 18 }}>
          {PUBLIC_ENDPOINTS.map((e) => (
            <EndpointCard key={e.path} ep={e} />
          ))}
        </div>
      </Section>

      <Section id="rest">
        <SectionHeading eyebrow="REST · RGS" title="Game endpoints" lead="Base URL https://vaultbreaker-api.fly.dev" />
        <div style={{ display: "grid", gap: 18 }}>
          {ENDPOINTS.map((e) => (
            <EndpointCard key={e.path} ep={e} />
          ))}
        </div>
      </Section>

      <Section id="admin">
        <SectionHeading
          eyebrow="Admin"
          title="Operator config + delivery audit"
          lead="Webhook registration, payout-wallet management, and the delivery log. Bearer-token authenticated."
        />
        <div style={{ display: "grid", gap: 18 }}>
          {ADMIN_ENDPOINTS.map((e) => (
            <EndpointCard key={e.path} ep={e} />
          ))}
        </div>
      </Section>

      <Section id="websocket">
        <SectionHeading
          eyebrow="WebSocket"
          title="/game/stream"
          lead="Authenticate with the launch token, then receive server-authored events."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {WS_EVENTS.map(([type, blurb, payload], i) => (
            <div
              key={type}
              style={{
                padding: "14px 22px",
                borderTop: i === 0 ? "none" : "1px solid var(--line)",
                display: "grid",
                gridTemplateColumns: "160px 1fr",
                gap: 16,
                alignItems: "start",
              }}
            >
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--cyan)" }}>
                {type}
              </span>
              <div>
                <p style={{ margin: "0 0 4px", fontSize: 13, color: "var(--fg-2)" }}>{blurb}</p>
                <code style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-3)" }}>
                  {payload}
                </code>
              </div>
            </div>
          ))}
        </Card>
      </Section>

      <Section id="iframe">
        <SectionHeading
          eyebrow="Iframe embed"
          title="Drop the game into a casino lobby"
          lead="The /play route renders the full game in a sandboxed iframe."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "20px 24px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >{`<iframe
  src="https://vault-breaker-nine.vercel.app/play?operator=acme"
  allow="autoplay; clipboard-read"
  style="width:100%;aspect-ratio:16/10;border:0"
></iframe>`}</pre>
        </Card>
        <p style={{ fontSize: 13, color: "var(--fg-3)", marginTop: 12, lineHeight: 1.55 }}>
          The iframe sends a launch request to <code>/rgs/session/launch</code> with the operator
          you pass via the URL. To set the player's wallet from the host page, use{" "}
          <code>postMessage</code>:
        </p>
        <Card style={{ padding: 0, overflow: "hidden", marginTop: 12 }}>
          <pre
            style={{
              margin: 0,
              padding: "20px 24px",
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >{`// host page → iframe
iframe.contentWindow.postMessage(
  { type: "SET_BALANCE", value: 0.5 },
  "*"
);

iframe.contentWindow.postMessage(
  { type: "SET_OPERATOR", value: "acme", locale: "en" },
  "*"
);`}</pre>
        </Card>
      </Section>

      <Section id="launch-token">
        <SectionHeading
          eyebrow="Roadmap"
          title="Signed launch tokens"
          lead="Plaintext ?operator= is fine for the public demo, not for real player traffic. Signed JWTs land next sprint."
        />
        <Card>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            The next API revision will accept <code>?launch=&lt;jwt&gt;</code> in the iframe URL,
            with the JWT signed by your operator API key (HMAC-SHA256). Payload is{" "}
            <code>{`{ iss: operatorId, sub: playerId, cur, loc, bal, iat, exp }`}</code>{" "}
            with a 5-minute max TTL. Server validates the signature and populates the session
            from the encoded fields.
          </p>
        </Card>
      </Section>

      <Section>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/sdk" style={{ textDecoration: "none" }}>
            <Button>SDK & TYPES →</Button>
          </Link>
          <Link to="/webhooks" style={{ textDecoration: "none" }}>
            <Button>WEBHOOKS →</Button>
          </Link>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <Button variant="gold">CONTACT SALES</Button>
          </Link>
        </div>
      </Section>
    </>
  );
}

function AuthRow({ scope, header, note }: { scope: string; header: string; note: string }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 14, alignItems: "start" }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          padding: "3px 8px",
          borderRadius: 4,
          background: "var(--bg-3)",
          border: "1px solid var(--line)",
          color: "var(--fg-1)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          textAlign: "center",
          alignSelf: "start",
        }}
      >
        {scope}
      </span>
      <div>
        <code className="mono" style={{ color: "var(--cyan)", fontSize: 13 }}>
          {header}
        </code>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--fg-2)" }}>{note}</p>
      </div>
    </div>
  );
}

function EndpointCard({ ep }: { ep: ApiEndpoint }) {
  const methodColors: Record<ApiEndpoint["method"], { bg: string; fg: string; border: string }> = {
    POST: { bg: "rgba(0,229,255,0.14)", fg: "var(--cyan)", border: "rgba(0,229,255,0.3)" },
    GET: { bg: "rgba(0,217,126,0.14)", fg: "var(--green)", border: "rgba(0,217,126,0.3)" },
    PUT: { bg: "rgba(245,197,109,0.14)", fg: "var(--gold-bright)", border: "rgba(245,197,109,0.3)" },
  };
  const mc = methodColors[ep.method];
  return (
    <Card style={{ padding: 0, overflow: "hidden" }}>
      <div
        style={{
          padding: "16px 22px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderBottom: "1px solid var(--line)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            padding: "3px 8px",
            borderRadius: 4,
            background: mc.bg,
            color: mc.fg,
            border: `1px solid ${mc.border}`,
          }}
        >
          {ep.method}
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--fg-1)" }}>
          {ep.path}
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            padding: "2px 6px",
            borderRadius: 3,
            background: "var(--bg-3)",
            color: "var(--fg-3)",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {ep.auth}
        </span>
      </div>

      <div style={{ padding: "16px 22px", display: "grid", gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>{ep.blurb}</p>

        {ep.request && (
          <div>
            <span className="label" style={{ fontSize: 10, color: "var(--fg-3)" }}>Request</span>
            <CodeBlock>{ep.request}</CodeBlock>
          </div>
        )}

        <div>
          <span className="label" style={{ fontSize: 10, color: "var(--fg-3)" }}>Response</span>
          <CodeBlock>{ep.response}</CodeBlock>
        </div>

        <div>
          <span className="label" style={{ fontSize: 10, color: "var(--fg-3)" }}>Error codes</span>
          <ul style={{ margin: "6px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 4 }}>
            {ep.errors.map(([code, blurb]) => (
              <li key={code} style={{ fontSize: 12, color: "var(--fg-2)", fontFamily: "var(--font-mono)" }}>
                <span style={{ color: "var(--red)" }}>{code}</span> · {blurb}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <span className="label" style={{ fontSize: 10, color: "var(--fg-3)" }}>curl</span>
          <CodeBlock>{ep.curl}</CodeBlock>
        </div>
      </div>
    </Card>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre
      style={{
        margin: "6px 0 0",
        padding: "10px 14px",
        background: "var(--bg-1)",
        border: "1px solid var(--line)",
        borderRadius: 6,
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        color: "var(--fg-1)",
        overflow: "auto",
        whiteSpace: "pre",
        lineHeight: 1.6,
      }}
    >
      {children}
    </pre>
  );
}
